import {
  Box,
  styled,
  Typography,
  Autocomplete,
  TextField,
  Button,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useLoadDataList from 'src/hook/useLoadDataList';
import {
  getStocks,
  StockDto,
  updateStock,
} from 'src/services/stock';
import { handleMutation } from 'src/utils/api.utils';

type StockOption = {
  id: string;
  name: string;
  quantity: number;
  price: number;
  granits: string[];
};

const toStockOptions = (stocks: StockDto[]): StockOption[] =>
  stocks.map((stock) => {
    const piecesGranits = stock.stockPieces?.map((p) => p.granit?.name);
    const stelesGranits = stock.stockSteles?.map((s) => s.granit?.name);
    const platingsGranits = stock.stockPlatings?.map((p) => p.granit?.name);
    const solesGranits = stock.stockSoles?.map((s) => s.granit?.name);

    const granits = [
      ...new Set(
        [
          ...(piecesGranits || []),
          ...(stelesGranits || []),
          ...(platingsGranits || []),
          ...(solesGranits || []),
        ].filter(Boolean),
      ),
    ] as string[];

    return {
      id: stock.id ?? '',
      name: stock.name,
      quantity: stock.quantity,
      price: stock.price,
      granits,
    };
  });

export const EditStock = () => {
  const { data: stocks, onRefresh } = useLoadDataList(getStocks);

  const stockOptions = useMemo(
    () => toStockOptions((stocks as StockDto[]) || []),
    [stocks],
  );

  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [name, setName] = useState('');

  const [selectedStock, setSelectedStock] = useState<string>('');

  const handleUpdate = (id: string) => {
    handleMutation(
      (data) => updateStock(id, data),
      {
        quantity: Number.parseInt(quantity, 10),
        price: Number.parseFloat(price),
        name,
      },
      () => {
        toast.success('Stock supprimé avec succès');
        onRefresh();
      },
    );
  };

  const handleStockChange = (stock: StockOption | null) => {
    setSelectedStock(stock?.id ?? '');
    setQuantity(stock?.quantity.toString() ?? '0');
    setPrice(stock?.price.toString() ?? '0');
    setName(stock?.name ?? '');
  };

  return (
    <Container>
      <Typography variant='h6' gutterBottom>
        Liste des stocks
      </Typography>
      <Autocomplete
        fullWidth
        options={stockOptions}
        value={stockOptions.find((s) => s.id === selectedStock) ?? null}
        onChange={(_event, newValue) => handleStockChange(newValue)}
        getOptionLabel={(option) =>
          option.granits.length > 0
            ? `${option.name} (${option.granits.join(', ')})`
            : option.name
        }
        filterOptions={(options, { inputValue }) => {
          const search = inputValue.toLowerCase();
          return options.filter(
            (option) =>
              option.name.toLowerCase().includes(search) ||
              option.granits.some((g) => g.toLowerCase().includes(search)),
          );
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Choisir un assemblage'
            required
            placeholder='Rechercher un stock...'
          />
        )}
        renderOption={(props, stock) => (
          <li {...props} key={stock.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px' }}>
            <div>
              <Typography variant='body1'>{stock.name}</Typography>
              {stock.granits.length > 0 && (
                <Typography variant='body2' color='text.secondary'>
                  {stock.granits.join(', ')}
                </Typography>
              )}
            </div>
          </li>
        )}
        ListboxProps={{
          style: {
            maxHeight: '300px',
          },
        }}
        noOptionsText='Aucun stock trouvé'
      />
      <TextField
        fullWidth
        label='Quantité'
        type='number'
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        required
        inputProps={{ min: 1 }}
      />

      <TextField
        fullWidth
        label='Prix'
        type='number'
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        required
        inputProps={{ min: 0, step: 1 }}
      />

      <TextField
        fullWidth
        label='Nom'
        type='text'
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />

      <Button
        type='submit'
        variant='contained'
        color='primary'
        fullWidth
        onClick={() => handleUpdate(selectedStock)}
      >
        Modifier le stock
      </Button>
    </Container>
  );
};

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: '20px',
});

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const Row = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});
